import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ConfirmModalFooter, Modal, Text } from '@wapharma/design-system';

export type WizardCloseGuardOptions = {
  open: boolean;
  dirty: boolean;
  /** Final discard / close (no confirm). */
  onClose: () => void;
};

export type WizardCloseGuard = {
  /** Esc / backdrop / X / Annuler — confirms when dirty. */
  requestClose: () => void;
  /** Pass through to `Drawer` when using the primitive directly. */
  closeGuard: boolean;
  onCloseGuard: () => void;
  confirmModal: ReactNode;
};

/**
 * Confirm before discarding a dirty create/import wizard.
 * Also arms native `beforeunload` while open + dirty.
 */
export function useWizardCloseGuard({
  open,
  dirty,
  onClose,
}: WizardCloseGuardOptions): WizardCloseGuard {
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) setConfirmOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open || !dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [open, dirty]);

  const openConfirm = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirmOpen(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);

  const confirmDiscard = useCallback(() => {
    setConfirmOpen(false);
    onClose();
  }, [onClose]);

  const cancelDiscard = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const confirmModal = (
    <Modal
      opened={confirmOpen}
      onClose={cancelDiscard}
      title="Quitter ?"
      footer={
        <ConfirmModalFooter
          cancelLabel="Rester"
          confirmLabel="Quitter"
          danger
          onCancel={cancelDiscard}
          onConfirm={confirmDiscard}
        />
      }
    >
      <Text size="sm">Les informations saisies seront perdues.</Text>
    </Modal>
  );

  return {
    requestClose,
    closeGuard: dirty,
    onCloseGuard: openConfirm,
    confirmModal,
  };
}
